'use client';

import { ReactNode } from 'react';
import type { BreadcrumbItem } from '@/components/molecules/Breadcrumb';
import { RelatedContentCard, type RelatedContentItem } from '@/components/molecules/RelatedContentCard';
import { PageHeroSection } from '@/components/organisms/shared/PageHeroSection';
import { ContactContent, type ContactInfo } from '@/components/organisms/level3/ContactContent';
import styles from './Level3Layout.module.scss';


export interface Level3LayoutProps {
  variant?: 'content' | 'interactive' | 'contact';
  title: string;
  description: string; // Made required to match PageHeroSection
  breadcrumbs?: BreadcrumbItem[];
  metadata?: {
    publishedAt?: string;
    updatedAt?: string;
    author?: string;
    readingTime?: string;
    category?: string;
    tags?: string[];
    date?: string;
  };
  children: ReactNode;
  relatedContent?: RelatedContentItem[];
  // Contact specific props
  contactInfo?: ContactInfo;
  // Flexible middle section component
  middleSection?: ReactNode;
}

export function Level3Layout({
  variant = 'content',
  title,
  description,
  breadcrumbs,
  metadata,
  children,
  relatedContent,
  contactInfo,
  middleSection
}: Level3LayoutProps) {

  return (
    <div id="top" className={`${styles.layoutWrapper} ${styles[variant]}`}>
      {/* Page Hero Section */}
      <PageHeroSection
        title={title}
        description={description}
        breadcrumbs={breadcrumbs || [{ label: title }]}
        metadata={metadata}
        level={3}
      />


      {/* Contact variant: Display content directly */}
      {variant === 'contact' && (
        <ContactContent contactInfo={contactInfo}>
          {children}
        </ContactContent>
      )}

      {/* Non-contact variants: Display content directly */}
      {variant !== 'contact' && (
        <div className={styles.mainContent}>
          <div className={styles.contentWrapper}>
            {children}
          </div>
        </div>
      )}

      {/* Flexible middle section */}
      {middleSection && (
        <section className={styles.middleSection}>
          {middleSection}
        </section>
      )}

      {/* Related Content */}
      {relatedContent && relatedContent.length > 0 && (
        <section id="related" className={styles.relatedContent}>
          <h2 className={styles.relatedTitle}>{variant === 'contact' ? '関連するお問い合わせ' : '関連コンテンツ'}</h2>
          {variant === 'contact' && (
            <p className={styles.relatedDescription}>
              他のお問い合わせフォームもご確認ください
            </p>
          )}
          <div className={styles.relatedGrid}>
            {relatedContent.map((item, index) => (
              <RelatedContentCard key={index} item={item} />
            ))}
          </div>
        </section>
      )}



    </div>
  );
}

export default Level3Layout;